/*
 * Der HMI-Editor: `/plants/:id/hmi/editor`.
 *
 * Das Gegenstueck zu `/plants/:id/hmi`. Dort laeuft ein gespeichertes Bild
 * gegen die echten Werte, hier wird es gebaut.
 *
 * <b>Raster oder frei, je Bild und Ziel.</b> Das Raster (HmiCanvas) fliesst mit
 * der Breite mit und ist fuer Browser und Handy hoch der Normalfall. Fuer
 * Panels mit fester Flaeche (TP8 und TP10 Pro quer und hoch) und das Handy
 * quer gibt es seit docs/hmi-panel-ausbau.md §2 die freie Anordnung
 * (FreeCanvas): pixelgenau, mit Ebenen und Hintergrund. Eine freie Anordnung
 * ist nur fuer genau eine Flaechengroesse richtig, deshalb gilt sie je Ziel,
 * und die Laufzeit skaliert sie gleichmaessig auf das Fenster.
 *
 * Aufbau von aussen nach innen:
 *
 *   AppShell und PlantSubNav
 *     Projektwaehler und Werkzeugleiste (Speichern, Rueckgaengig, Befunde)
 *       Bilderleiste und Zielleiste
 *         LivePlantBindingProvider
 *           Palette | Rasterflaeche | Eigenschaftsfeld
 *
 * <b>Warum die Live-Werte auch im Editor laufen.</b> Ein Autor soll sehen, was
 * er baut, und ein Gauge ohne Wert sieht in jeder Fehlkonfiguration gleich aus.
 * Die Flaeche schaltet die Widgets dafuer bedienungsblind (`pointer-events`),
 * damit ein Klick eine Auswahl bleibt und kein Schaltbefehl wird.
 */

import type { ComponentChildren, VNode } from 'preact';
import { useCallback, useEffect, useMemo, useState } from 'preact/hooks';

import { AppShell } from '../components/AppShell';
import { AiHmiModal } from '../components/ai/AiHmiModal';
import type { AppendSuggestionResult } from '../components/ai/hmiSuggestionMerge';
import { PlantSubNav } from '../components/PlantSubNav';
import { Card, CardBody, CardHeader } from '../components/ui/Card';
import { showConfirm } from '../components/Confirmer';
import { showToast } from '../components/Toaster';
import { useAsync } from '../hooks/useAsync';
import { api, ApiError } from '../api/client';
import type { PlantSchemaResponse } from '../api/types';
import { sessionSignal } from '../auth/session';

import { LivePlantBindingProvider } from '../hmi/PointBindingProvider';
import { HmiCanvas } from '../hmi/editor/HmiCanvas';
import { FreeCanvas, freeSlotFor } from '../hmi/editor/FreeCanvas';
import { DEFAULT_RUNTIME, HmiRuntimeProvider, type HmiRuntime } from '../hmi/panel/runtimeContext';
import { WidgetPalette } from '../hmi/editor/palette/WidgetPalette';
import { paletteItem, type PaletteItem } from '../hmi/editor/palette/paletteItems';
import { SideDock } from '../hmi/editor/SideDock';
import { PropertyPanel } from '../hmi/editor/properties/PropertyPanel';
import {
  GRID_SPECS,
  LAYOUT_TARGET_ORDER,
  addScreen,
  addWidget,
  duplicateScreen,
  findFreeSlot,
  findWidget,
  freshId,
  isFreeLayout,
  layoutOf,
  moveScreen,
  setLayoutMode,
  removeScreen,
  removeWidget,
  renameScreen,
  replaceWidget,
  widgetIds as allWidgetIds,
  type ValidationFinding,
} from '../hmi/editor/editorModel';
import { createEditorStore, type EditorStore } from '../hmi/editor/editorStore';
import { TargetPreview } from '../hmi/editor/TargetPreview';
import {
  createHmiProject,
  getHmiProject,
  listHmiProjects,
  parseProjectBody,
  serialiseProjectBody,
  type HmiProjectDetail,
  type HmiProjectSummary,
} from '../hmi/projectApi';
import type { HmiProject, LayoutTarget, WidgetKind } from '../hmi/widgets';
import './HmiEditorPage.css';
import { BRAND } from '../brand/brand';
import { buildLeafMetaIndex } from '../hmi/leafMeta';
import { LeafMetaProvider, useLeafMetaLookup } from '../hmi/LeafMetaContext';
import { applyLeafDefaults } from '../hmi/editor/leafDefaults';
import { HmiAppearanceMenu } from '../hmi/HmiAppearanceMenu';
import { NotesDialog } from '../hmi/editor/NotesDialog';

interface Props {
  /** Route-Parameter `:id`. */
  id?: string;
}

export function HmiEditorPage({ id }: Props) {
  const plantId = id ?? '';
  const canEdit = sessionSignal.value.user?.permissions.includes('plants.schema.edit') ?? false;

  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);
  const [refreshTick, setRefreshTick] = useState(0);
  // Der zweite Einstieg ins KI-Modal: der fuer die Anlage, die noch gar kein
  // Projekt hat. Er muss hier oben liegen, weil es unten keinen Editor gibt,
  // an den er sich haengen koennte, und er kennt folglich nur den Weg
  // "anlegen".
  const [aiForEmpty, setAiForEmpty] = useState(false);

  const projectsState = useAsync(
    (signal) => (plantId ? listHmiProjects(plantId, signal) : Promise.resolve({ projects: [] })),
    [plantId, refreshTick],
  );
  const projects: HmiProjectSummary[] = projectsState.status === 'ok'
    ? projectsState.data.projects
    : [];

  useEffect(() => {
    if (projects.length === 0) {
      if (selectedProjectId !== null) setSelectedProjectId(null);
      return;
    }
    if (!selectedProjectId || !projects.some((p) => p.id === selectedProjectId)) {
      setSelectedProjectId(projects[0].id);
    }
  }, [projects, selectedProjectId]);

  const detailState = useAsync(
    (signal) => (selectedProjectId
      ? getHmiProject(selectedProjectId, signal)
      : Promise.resolve(null)),
    [selectedProjectId, refreshTick],
  );
  const detail = detailState.status === 'ok' ? detailState.data : null;

  // Das Anlagenschema braucht die Datenpunkt-Auswahl. Scheitert es, laeuft der
  // Editor weiter: platzieren geht ohne Schema, nur binden nicht.
  //
  // Second purpose: leaf metadata (unit, enum texts, limits) for the widgets
  // on the canvas and for the defaults on first binding. Without a schema
  // both drop out silently and the widgets show exactly what the project says.
  const schemaState = useAsync(
    (signal) => (plantId
      ? api.get<PlantSchemaResponse>(`/api/plants/${plantId}/schema`, signal)
      : Promise.resolve(null)),
    [plantId],
  );
  const leafMetaIndex = useMemo(
    () => buildLeafMetaIndex(schemaState.status === 'ok' ? schemaState.data : null),
    [schemaState.status === 'ok' ? schemaState.data : null],
  );
  const leafMetaByLeafId = useCallback((leafId: string) => leafMetaIndex.get(leafId), [leafMetaIndex]);

  const chooser = (
    <ProjectChooser
      projects={projects}
      selectedId={selectedProjectId}
      canEdit={canEdit}
      plantId={plantId}
      onSelect={setSelectedProjectId}
      onCreated={(created) => {
        setSelectedProjectId(created.id);
        setRefreshTick((t) => t + 1);
      }}
    />
  );

  return (
    <LeafMetaProvider byLeafId={leafMetaByLeafId}>
      <AppShell>
        <PlantSubNav plantId={plantId} />
        <div class="hv-hmi-editor">
          {/* Kein Seitenkopf mehr (04.11.2026): Titel und Erklaersatz kosteten
              eine Handbreit Hoehe, die der Flaeche fehlte. Die Projektwahl
              steht vorne in der Werkzeugleiste; nur ohne offenes Projekt
              steht sie allein hier. */}
          {!detail && (
            <div class="hv-hmi-editor__bar">
              {chooser}
            </div>
          )}

          {!canEdit && (
            <p class="hv-hmi-editor__note hv-hmi-editor__note--warn">
              Zum Bearbeiten fehlt die Berechtigung <code>plants.schema.edit</code>.
              Die Seite zeigt den Stand, speichern kannst du nicht.
            </p>
          )}

          {projectsState.status === 'loading' && <p class="hv-hmi-editor__note">Projekte werden geladen.</p>}
          {projectsState.status === 'error' && (
            <p class="hv-hmi-editor__note hv-hmi-editor__note--warn">
              Die Projektliste ließ sich nicht laden: {describeError(projectsState.error)}
            </p>
          )}
          {projectsState.status === 'ok' && projects.length === 0 && (
            <Card>
              <CardHeader><h2>Noch kein HMI-Projekt</h2></CardHeader>
              <CardBody>
                <p>
                  Diese Anlage hat noch kein HMI-Projekt. Leg eines an, dann
                  kannst du das erste Bild bauen.
                </p>
                {canEdit && (
                  <p>
                    <button type="button" class="hv-btn hv-hmi-editor__ai"
                            onClick={() => setAiForEmpty(true)}>
                      {BRAND.aiName} · Bild vorschlagen lassen
                    </button>
                    {' '}
                    <span class="muted">
                      Die KI sieht sich die Datenpunkte an und legt daraus ein erstes
                      Projekt an. Ändern kannst du danach alles.
                    </span>
                  </p>
                )}
              </CardBody>
            </Card>
          )}

          {aiForEmpty && (
            <AiHmiModal
              plantId={plantId}
              openProject={null}
              existingProjectNames={projects.map((p) => p.name)}
              onAppend={() => { /* ohne offenes Projekt gibt es diesen Weg nicht */ }}
              onCreated={(created) => {
                setSelectedProjectId(created.id);
                setRefreshTick((t) => t + 1);
                showToast({
                  tone: 'success',
                  title: `Projekt "${created.name}" angelegt`,
                  message: 'Der Vorschlag steht jetzt im Editor. Sieh ihn dir an und ändere, was nicht passt.',
                  autoHideAfterMs: 6000,
                });
              }}
              onClose={() => setAiForEmpty(false)}
            />
          )}

          {detailState.status === 'error' && (
            <p class="hv-hmi-editor__note hv-hmi-editor__note--warn">
              Das Projekt ließ sich nicht laden: {describeError(detailState.error)}
            </p>
          )}

          {detail && (
            <EditorForProject
              key={`${detail.id}:${detail.revision}`}
              barStart={chooser}
              detail={detail}
              plantId={plantId}
              canEdit={canEdit}
              projectNames={projects.map((p) => p.name)}
              onReloaded={() => setRefreshTick((t) => t + 1)}
              onProjectCreated={(created) => {
                setSelectedProjectId(created.id);
                setRefreshTick((t) => t + 1);
              }}
            />
          )}
        </div>
      </AppShell>
    </LeafMetaProvider>
  );
}

// --------------------------------------------------------------- Projektwahl

function ProjectChooser(props: {
  projects: readonly HmiProjectSummary[];
  selectedId: string | null;
  canEdit: boolean;
  plantId: string;
  onSelect(id: string): void;
  onCreated(detail: HmiProjectDetail): void;
}) {
  const [creating, setCreating] = useState(false);

  async function onCreate() {
    if (creating) return;
    setCreating(true);
    try {
      const created = await createHmiProject(props.plantId, {
        name: suggestProjectName(props.projects),
        description: 'Im Editor angelegt.',
      });
      showToast({ tone: 'success', title: `Projekt "${created.name}" angelegt`, autoHideAfterMs: 4000 });
      props.onCreated(created);
    } catch (err) {
      showToast({
        tone: 'error',
        title: 'Anlegen fehlgeschlagen',
        message: describeError(err),
        autoHideAfterMs: 6000,
      });
    } finally {
      setCreating(false);
    }
  }

  return (
    <div class="hv-hmi-editor__chooser">
      <label>
        <span>Projekt</span>
        <select
          value={props.selectedId ?? ''}
          onChange={(e) => props.onSelect((e.currentTarget as HTMLSelectElement).value)}
          disabled={props.projects.length === 0}
        >
          {props.projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      </label>
      {props.canEdit && (
        <button type="button" class="hv-btn" onClick={onCreate} disabled={creating}>
          {creating ? 'Wird angelegt…' : 'Neues Projekt'}
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------- Der Editor

function EditorForProject(props: {
  detail: HmiProjectDetail;
  plantId: string;
  canEdit: boolean;
  /** Fuer die Namensvorbelegung im KI-Modal: zwei Projekte gleichen Namens
   *  weist der Server mit HTTP 409 ab. */
  projectNames: readonly string[];
  onReloaded(): void;
  onProjectCreated(detail: HmiProjectDetail): void;
  barStart?: ComponentChildren;
}) {
  const { detail, plantId, canEdit } = props;

  const parsed = useMemo(() => parseProjectBody(detail.bodyJson), [detail.bodyJson]);

  // Der Store haengt am Projekt und an seinem Stand. Der `key` an der
  // Komponente sorgt dafuer, dass ein Projektwechsel eine NEUE Instanz
  // erzeugt; ein Store, der einen Wechsel ueberlebt, traegt die
  // Rueckgaengig-Kette der vorigen Bearbeitung mit hinueber.
  const store = useMemo<EditorStore | null>(() => {
    if (!parsed.ok) return null;
    const project: HmiProject = {
      id: detail.id,
      name: detail.name,
      description: detail.description ?? undefined,
      schemaVersion: detail.schemaVersion,
      widgets: parsed.widgets,
      // Ein frisch angelegtes Projekt hat einen leeren Rumpf und damit kein
      // Bild. Ohne ein erstes Bild gibt es keine Flaeche, auf die man etwas
      // legen koennte, und der Autor muesste vor dem Zeichnen eine
      // Verwaltungsaufgabe loesen. Also eines beilegen. Der Editor steht
      // dadurch von Anfang an auf "nicht gespeichert", und das stimmt auch:
      // dieses Bild gibt es auf dem Server noch nicht.
      screens: parsed.screens.length > 0
        ? parsed.screens
        : [{ id: 'screen-1', label: 'Bild 1', widgetIds: [], layouts: [] }],
      appearance: parsed.appearance,
      notes: parsed.notes,
    };
    return createEditorStore({
      projectId: detail.id,
      revision: detail.revision,
      project,
      name: detail.name,
      description: detail.description,
      rest: parsed.rest,
      // Der Serverstand als Vergleichsgrundlage, NICHT der Entwurf. Das
      // beigelegte erste Bild ist eine Aenderung und soll als solche
      // dastehen.
      savedBodyJson: serialiseProjectBody(parsed.widgets, parsed.screens, parsed.rest, parsed.appearance, parsed.notes),
    });
  }, [parsed, detail.id, detail.revision]);

  // Ungespeichertes beim Verlassen des Browserfensters melden. Nicht huebsch,
  // aber die einzige Stelle, an der der Browser ueberhaupt fragt. Ein
  // versehentlich geschlossener Reiter kostet sonst die ganze Anordnung.
  useEffect(() => {
    if (!store) return;
    const onBeforeUnload = (ev: BeforeUnloadEvent) => {
      if (!store.dirty.peek()) return;
      ev.preventDefault();
      ev.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [store]);

  const onSave = useCallback(async () => {
    if (!store) return;
    const result = await store.save();
    if (result.status === 'saved') {
      showToast({ tone: 'success', title: `Gespeichert, Stand ${result.revision}`, autoHideAfterMs: 3000 });
      return;
    }
    if (result.status === 'blocked') {
      showToast({
        tone: 'error',
        title: 'Nicht gespeichert',
        message: result.findings.map((f) => f.message).join(' '),
        autoHideAfterMs: 8000,
      });
      return;
    }
    if (result.status === 'conflict') {
      const who = result.conflict.updatedBy ? ` von ${result.conflict.updatedBy}` : '';
      const when = result.conflict.updatedAt
        ? ` am ${new Date(result.conflict.updatedAt).toLocaleString('de-AT')}`
        : '';
      const takeServer = await showConfirm({
        title: 'Jemand anders war schneller',
        message:
          `Dieses Projekt wurde${who}${when} gespeichert und steht jetzt auf Stand ` +
          `${result.conflict.currentRevision ?? '?'}, deiner baut auf ` +
          `${result.conflict.submittedRevision ?? '?'} auf. ` +
          'Die fremde Fassung laden heißt: deine Änderungen an diesem Bild sind weg. ' +
          'Abbrechen lässt deinen Stand stehen, damit du ihn sichern kannst.',
        confirmLabel: 'Fremde Fassung laden',
        cancelLabel: 'Meinen Stand behalten',
        danger: true,
      });
      if (takeServer) props.onReloaded();
      return;
    }
    showToast({
      tone: 'error',
      title: 'Speichern fehlgeschlagen',
      message: result.message,
      autoHideAfterMs: 8000,
    });
  }, [store, props]);

  if (!parsed.ok) {
    return (
      <Card>
        <CardHeader><h2>Projekt-Rumpf ist unlesbar</h2></CardHeader>
        <CardBody>
          <p class="hv-hmi-editor__note hv-hmi-editor__note--warn">{parsed.error}</p>
          <p>
            Der Editor lässt das Projekt bewusst unangetastet: überschriebe er
            es mit einem leeren Rumpf, wäre der bisherige Inhalt endgültig weg.
          </p>
        </CardBody>
      </Card>
    );
  }
  if (!store) return null;

  return (
    <EditorBody
      store={store}
      plantId={plantId}
      canEdit={canEdit}
      projectNames={props.projectNames}
      onSave={onSave}
      onProjectCreated={props.onProjectCreated}
      barStart={props.barStart}
    />
  );
}

export function EditorBody(props: {
  store: EditorStore;
  plantId: string;
  canEdit: boolean;
  projectNames: readonly string[];
  onSave(): void;
  onProjectCreated(detail: HmiProjectDetail): void;
  /** Where the values come from. Default: the plant's live values. The
   *  dev harness (hmi-editor-dev.html) puts a simulator here. */
  wrapSource?: (children: ComponentChildren) => VNode;
  /** Vorne in der Werkzeugleiste, etwa die Projektwahl. */
  barStart?: ComponentChildren;
}) {
  const { store, plantId, canEdit } = props;
  const leafMetaLookup = useLeafMetaLookup();

  const project = store.project.value;
  const screenId = store.activeScreenId.value;
  const target = store.activeTarget.value;
  const selection = store.selection.value;
  const primary = store.primarySelection.value;
  const findings = store.findings.value;
  const errors = findings.filter((f) => f.severity === 'error');
  const warnings = findings.filter((f) => f.severity === 'warning');

  // Die Befundliste ist zugeklappt, bis jemand sie aufmacht. Aufgeklappt
  // schiebt sie die Flaeche nach unten, und die Flaeche ist das, wofuer der
  // Autor hier ist.
  const [showFindings, setShowFindings] = useState(false);
  // Die Vorschau ueber alle Ziele. Steht hier und nicht im Store: sie aendert
  // nichts am Projekt, gehoert also nicht in den Rueckgaengig-Stapel.
  const [previewOpen, setPreviewOpen] = useState(false);
  const [notesOpen, setNotesOpen] = useState(false);
  const [aiOpen, setAiOpen] = useState(false);

  // Das Bild, auf das sich Flaeche und Vorschau beziehen. Kann fehlen,
  // solange ein frisch geladenes Projekt noch kein aktives Bild hat.
  const activeScreen = project.screens.find((sc) => sc.id === screenId) ?? null;

  const selected = primary ? findWidget(project, primary) : null;

  /**
   * Einen KI-Vorschlag in den Entwurf legen.
   *
   * Ueber `store.apply` und damit rueckgaengig-faehig, und ausdruecklich OHNE
   * Speichern: der Autor soll den Vorschlag ansehen, aendern und dann selbst
   * speichern. Ein Vorschlag, der sich selbst speichert, nimmt ihm genau den
   * Schritt weg, an dem er ihn noch ablehnen koennte.
   *
   * Hingeschaltet wird auf das neue Bild und auf den Desktop, denn nur fuer
   * dieses Ziel bringt ein Vorschlag eine Anordnung mit. Stuende die Ansicht
   * danach auf einem Panel, saehe der Autor sein neues Bild im Auto-Flow und
   * hielte den Vorschlag fuer misslungen.
   *
   * Die Meldung nennt die umbenannten Ids. Eine stille Umbenennung waere
   * genau die Art Aenderung, die erst auffaellt, wenn jemand die Id im
   * gespeicherten JSON sucht und nicht findet.
   */
  /**
   * Ein KI-Vorschlag ist als eigenes Projekt entstanden.
   *
   * Hinschalten heisst, den offenen Entwurf fallen zu lassen. Solange er
   * gespeichert ist, kostet das nichts; ist er es nicht, waere es genau der
   * stille Verlust, gegen den der Editor sonst ueberall Vorkehrungen trifft.
   * Also fragen, und bei Nein hierbleiben: das neue Projekt ist angelegt und
   * steht im Waehler, es geht nichts verloren, wenn der Autor spaeter
   * hinschaltet.
   */
  const onAiProjectCreated = useCallback(async (created: HmiProjectDetail) => {
    if (store.dirty.peek()) {
      const wechseln = await showConfirm({
        title: `Zu "${created.name}" wechseln?`,
        message:
          'Das neue Projekt ist angelegt. Zu ihm zu wechseln heißt, den offenen, nicht '
          + 'gespeicherten Entwurf hier fallen zu lassen. Bleibst du, findest du das neue '
          + 'Projekt jederzeit im Projektwähler.',
        confirmLabel: 'Wechseln',
        cancelLabel: 'Hierbleiben',
        danger: true,
      });
      if (!wechseln) {
        showToast({
          tone: 'success',
          title: `Projekt "${created.name}" angelegt`,
          message: 'Du bleibst im offenen Entwurf. Das neue Projekt steht im Wähler oben.',
          autoHideAfterMs: 8000,
        });
        return;
      }
    }
    props.onProjectCreated(created);
  }, [store, props]);

  const takeSuggestion = useCallback((result: AppendSuggestionResult) => {
    store.apply(result.project);
    const first = result.addedScreenIds[0];
    if (first) store.activeScreenId.value = first;
    store.selection.value = [];
    store.activeTarget.value = 'desktopResponsive';

    const renamed = Object.keys(result.renamedWidgetIds).length;
    const notes: string[] = ['Das Bild steht im Entwurf und ist noch nicht gespeichert.'];
    if (renamed > 0) {
      notes.push(
        `${renamed} Widget-Id(s) waren hier schon vergeben und wurden umbenannt; `
        + 'die Platzierungen sind mit umgeschlüsselt.',
      );
    }
    if (result.droppedPlacements.length > 0) {
      notes.push(
        `${result.droppedPlacements.length} Platzierung(en) des Vorschlags zeigten auf `
        + 'kein Widget und wurden verworfen.',
      );
    }
    showToast({
      tone: 'success',
      title: 'Vorschlag übernommen',
      message: notes.join(' '),
      autoHideAfterMs: 9000,
    });
  }, [store]);

  // Ein Vorschlag gilt fuer genau ein Ziel. Wer wegschaltet, hat
  // ihn nicht bestaetigt; ihn beim Zurueckschalten wieder
  // hinzustellen waere eine Ueberraschung.
  const selectTarget = (t: LayoutTarget) => {
    store.proposal.value = null;
    store.activeTarget.value = t;
  };

  /** Ein neues Widget anlegen und platzieren. Einziger Ort, an dem aus einer
   *  Art ein Widget wird: die Palette kennt den Store nicht, die Flaeche legt
   *  bewusst nichts an. */
  /** Wie oft schon etwas aufgenommen wurde; eine nicht angeheftete
   *  Bausteinleiste klappt danach zu, damit die Flaeche frei wird. */
  const [picks, setPicks] = useState(0);

  const place = useCallback(
    (key: string | PaletteItem, at?: { col: number; row: number }, rect?: { x: number; y: number; w: number; h: number }) => {
      const entry = typeof key === 'string' ? paletteItem(key) : key;
      if (!entry) return;
      const kind: WidgetKind = entry.kind;
      setPicks((n) => n + 1);
      const current = store.project.peek();
      const activeScreen = store.activeScreenId.peek();
      const activeTarget = store.activeTarget.peek();

      const widget = entry.create(freshId(kind, allWidgetIds(current)));
      // Freie Anordnung: Pixel, nie eine Rasterzelle. Ohne Ablegepunkt (Klick
      // in der Palette) an die erste freie Stelle.
      const free = isFreeLayout(layoutOf(current, activeScreen, activeTarget));
      const placement = free
        ? { col: 0, row: 0, colSpan: entry.colSpan, rowSpan: entry.rowSpan,
            ...(rect ?? freeSlotFor(current, activeScreen, activeTarget, entry.key)) }
        : at
          ? { col: at.col, row: at.row, colSpan: entry.colSpan, rowSpan: entry.rowSpan }
          : findFreeSlot(current, activeScreen, activeTarget, entry.colSpan, entry.rowSpan);

      store.apply(addWidget(current, activeScreen, activeTarget, widget, placement));
      store.selection.value = [widget.id];
    },
    [store],
  );

  const activeFree = activeScreen
    ? isFreeLayout(activeScreen.layouts.find((l) => l.target === target))
    : false;

  /** Raster oder frei fuer dieses Bild und Ziel. Ein Schritt, rueckgaengig
   *  zu machen; die Umrechnung steht in `setLayoutMode`. */
  const switchMode = (mode: 'grid' | 'free') => {
    if (!activeScreen || mode === (activeFree ? 'free' : 'grid')) return;
    store.proposal.value = null;
    store.apply(setLayoutMode(store.project.peek(), activeScreen.id, target, mode));
  };

  /** What the free-design widgets see in the editor: the project's screens,
   *  the active one, and `editing` so a button never writes from here. */
  const runtime = useMemo<HmiRuntime>(() => ({
    ...DEFAULT_RUNTIME,
    screens: project.screens.map((s) => ({ id: s.id, label: s.label, iconRef: s.iconRef })),
    currentScreenId: screenId,
    navigate: (id: string) => { store.activeScreenId.value = id; },
    editing: true,
  }), [project.screens, screenId, store]);

  return (
    <>
      <div class="hv-hmi-editor__bar">
        {props.barStart}
        <button type="button" class="hv-btn hv-btn--primary" onClick={props.onSave}
                disabled={!canEdit || !store.dirty.value || store.saving.value || errors.length > 0}>
          {store.saving.value ? 'Speichert…' : 'Speichern'}
        </button>
        <button type="button" class="hv-btn" onClick={() => store.undo()} disabled={!store.canUndo.value}>
          Rückgängig
        </button>
        <button type="button" class="hv-btn" onClick={() => store.redo()} disabled={!store.canRedo.value}>
          Wiederholen
        </button>

        {/* Die Vorschau steht neben den Werkzeugen und nicht bei den
            Ziel-Reitern: sie ersetzt keinen Reiter, sie beantwortet die Frage,
            die zwischen den Reitern liegt. Kein Bearbeitungsrecht noetig, es
            wird nur angesehen. */}
        <button type="button" class="hv-btn" onClick={() => setPreviewOpen((open) => !open)}
                aria-pressed={previewOpen}>
          {previewOpen ? 'Vorschau schließen' : 'Vorschau alle Ziele'}
        </button>

        {/* Part of the project like the arrangement: undoable, saved with
            it, and the same for everybody who opens the HMI. */}
        <HmiAppearanceMenu
          value={project.appearance}
          disabled={!canEdit}
          onChange={(appearance, key) => store.apply({ ...store.project.peek(), appearance }, key)}
        />

        {/* Hinweis "Was ist neu" fuers Panel (18.10.2026). */}
        <button type="button" class="hv-btn" onClick={() => setNotesOpen(true)}>
          Neuigkeiten{project.notes?.length ? ` (${project.notes.length})` : ''}
        </button>
        {notesOpen && (
          <NotesDialog notes={project.notes ?? []} disabled={!canEdit} onClose={() => setNotesOpen(false)}
                       onChange={(notes) => store.apply({ ...store.project.peek(), notes: notes.length ? notes : undefined })} />
        )}

        {/* Der Einstieg ins KI-Modal steht in der Werkzeugleiste und nicht im
            Kopf der Seite: er gehoert zu dem, was am Entwurf geschieht, und
            nicht zur Projektverwaltung. Ohne Bearbeitungsrecht faellt er weg,
            weil beide Uebernahme-Wege es brauchen. */}
        {canEdit && (
          <button type="button" class="hv-btn hv-hmi-editor__ai" onClick={() => setAiOpen(true)}>
            {BRAND.aiName} · Bild vorschlagen
          </button>
        )}

        <span class={`hv-hmi-editor__state${store.dirty.value ? ' hv-hmi-editor__state--dirty' : ''}`}>
          {store.dirty.value ? 'Nicht gespeichert' : `Gespeichert, Stand ${store.revision.value}`}
        </span>

        {/* Die Befunde waren bis hierher nur ein Sprechblasentext. Das reicht
            zum Zaehlen und nicht zum Aufraeumen: ein Widget, das auf keinem
            Bild mehr liegt, ist ueberhaupt nur in dieser Meldung noch
            sichtbar, und ohne einen Weg von hier aus bleibt es im Projekt
            stehen. Deshalb sind die Zaehler jetzt Schalter. */}
        {errors.length > 0 && (
          <button type="button"
                  class="hv-hmi-editor__badge hv-hmi-editor__badge--error"
                  aria-expanded={showFindings}
                  onClick={() => setShowFindings((open) => !open)}>
            {errors.length} Fehler
          </button>
        )}
        {warnings.length > 0 && (
          <button type="button"
                  class="hv-hmi-editor__badge"
                  aria-expanded={showFindings}
                  onClick={() => setShowFindings((open) => !open)}>
            {warnings.length} Hinweise
          </button>
        )}
      </div>

      {showFindings && findings.length > 0 && (
        <FindingsList store={store} findings={findings} canEdit={canEdit} />
      )}

      {/* One live source for preview and canvas. The preview used to sit
          outside it and showed "Keine Datenquelle" on every tile. */}
      {(props.wrapSource ?? ((c: ComponentChildren) => (
        <LivePlantBindingProvider plantId={plantId}>{c}</LivePlantBindingProvider>
      )))(
       <HmiRuntimeProvider value={runtime}>
        {previewOpen && activeScreen && (
          <TargetPreview
            screen={activeScreen}
            widgets={project.widgets}
            appearance={project.appearance}
            onSelectTarget={(t) => { selectTarget(t); setPreviewOpen(false); }}
            onClose={() => setPreviewOpen(false)}
          />
        )}

        <div class="hv-hmi-editor__navRow">
        <ScreenBar store={store} canEdit={canEdit} />

        <div class="hv-hmi-editor__targetRow">
          <div class="hv-hmi-editor__targets" role="tablist" aria-label="Layout-Ziel">
            {LAYOUT_TARGET_ORDER.map((t) => {
              const free = activeScreen ? isFreeLayout(activeScreen.layouts.find((l) => l.target === t)) : false;
              return (
                <button
                  key={t}
                  type="button"
                  role="tab"
                  aria-selected={t === target}
                  class={`hv-hmi-editor__target${t === target ? ' is-active' : ''}`}
                  onClick={() => selectTarget(t)}
                >
                  {GRID_SPECS[t].label}
                  {free && <span class="hv-hmi-editor__freeMark" aria-label="frei">frei</span>}
                </button>
              );
            })}
          </div>

          {activeScreen && (
            <div class="hv-hmi-editor__mode" role="radiogroup" aria-label="Anordnung">
              <span class="hv-hmi-editor__modeLabel">Anordnung</span>
              <button type="button" role="radio" aria-checked={!activeFree}
                      class={`hv-hmi-editor__modeBtn${!activeFree ? ' is-active' : ''}`}
                      disabled={!canEdit}
                      title="Bausteine rasten in Zellen ein und fließen mit der Breite mit"
                      onClick={() => switchMode('grid')}>
                Raster
              </button>
              <button type="button" role="radio" aria-checked={activeFree}
                      class={`hv-hmi-editor__modeBtn${activeFree ? ' is-active' : ''}`}
                      disabled={!canEdit}
                      title="Pixelgenau auf der Fläche des Geräts, mit Ebenen und Hintergrund"
                      onClick={() => switchMode('free')}>
                Frei
              </button>
            </div>
          )}
        </div>

        </div>

        <div class="hv-hmi-editor__body">
          <SideDock
            side="left"
            title="Bausteine"
            storageKey="hv-hmi-dock-bausteine"
            // Drei Spalten Miniaturen: 3 x 112 + 2 x 8 Abstand, dazu Polster
            // und Bildlaufleiste.
            defaultWidth={404}
            minWidth={260}
            maxWidth={760}
            closeSignal={picks}
          >
            <WidgetPalette class="hv-hmi-editor__palette" onPick={(item) => place(item)} />
          </SideDock>

          <div class="hv-hmi-editor__canvas">
            {activeFree ? (
              <FreeCanvas
                store={store}
                canEdit={canEdit}
                onDropWidget={(key, rect) => place(key, undefined, rect)}
              />
            ) : (
              <HmiCanvas
                store={store}
                canEdit={canEdit}
                onDropWidget={(key, at) => place(key, at)}
              />
            )}
          </div>

          <SideDock
            side="right"
            title="Eigenschaften"
            storageKey="hv-hmi-dock-eigenschaften"
            defaultWidth={336}
            minWidth={260}
            maxWidth={640}
          >
          <PropertyPanel
            widget={selected}
            selectionCount={selection.length}
            plantId={plantId}
            // The empty panel carries the short guide that used to sit
            // below the canvas, where nobody read it.
            placeholder={(
              <div class="hv-hmi-editor__guide">
                <h3>Nichts ausgewählt</h3>
                <p>
                  Ein Widget auf der Fläche anklicken, oder links in der
                  Palette ein neues aufnehmen. Bearbeitet wird das Bild
                  {activeScreen ? ` "${activeScreen.label}"` : ''} für das
                  Ziel {GRID_SPECS[target].label}.
                </p>
                <dl>
                  <dt>Strg+Klick</dt>
                  <dd>Zur Auswahl hinzufügen. Das zuerst gewählte ist der Bezug beim Ausrichten.</dd>
                  <dt>Pfeiltasten</dt>
                  <dd>{activeFree ? 'Um 1 px verschieben, mit Umschalt um 10 px' : 'Um eine Zelle verschieben'}</dd>
                  <dt>Strg+D</dt>
                  <dd>Duplizieren</dd>
                  <dt>Entf</dt>
                  <dd>Löschen</dd>
                  <dt>Esc</dt>
                  <dd>Auswahl aufheben</dd>
                </dl>
              </div>
            )}
            onChange={(next) => {
              // On first binding, prefill caption, decimals and scale from
              // the leaf (hmi/editor/leafDefaults.ts).
              const prev = findWidget(store.project.peek(), next.id);
              const withDefaults = prev ? applyLeafDefaults(prev, next, leafMetaLookup) : next;
              // Ein Schluessel je Widget und Art: aufeinanderfolgende
              // Tastendruecke in einem Feld werden zu einem Schritt, ein
              // Wechsel auf ein anderes Widget beginnt einen neuen.
              store.apply(replaceWidget(store.project.peek(), withDefaults), `eigenschaft:${next.id}`);
            }}
          />
          </SideDock>
        </div>
       </HmiRuntimeProvider>,
      )}

      {aiOpen && (
        <AiHmiModal
          plantId={plantId}
          openProject={{ name: project.name, project }}
          existingProjectNames={props.projectNames}
          onAppend={takeSuggestion}
          onCreated={(created) => { void onAiProjectCreated(created); }}
          onClose={() => setAiOpen(false)}
        />
      )}
    </>
  );
}

// --------------------------------------------------------------- Befunde

/**
 * Die Befunde als Liste, mit einer Handhabe dort, wo es eine gibt.
 *
 * Der eigentliche Grund fuer diese Liste ist der Befund "liegt auf keinem
 * Bild": er war bisher eine Sackgasse. Gemeldet wird ein Widget, das in
 * keinem Bild vorkommt und damit nirgends anklickbar ist; es zaehlt trotzdem
 * bei der Datenpunkt-Zaehlung und in der Lizenzsicht mit. Wer es loswerden
 * wollte, musste das JSON von Hand anfassen. Der Weg dorthin gehoert an die
 * Stelle, an der die Warnung steht, und nicht in ein eigenes Menue: eine
 * Aufraeumfunktion, die man erst suchen muss, raeumt niemand auf.
 *
 * Nur dieser eine Befund bekommt eine Schaltflaeche, und das ist Absicht. Die
 * uebrigen sind entweder von selbst weg, sobald der Autor das Richtige tut
 * (ein leeres Bild), oder sie brauchen eine Entscheidung, die eine
 * Schaltflaeche nicht treffen kann (zwei gleiche Ids: welche der beiden ist
 * die gemeinte?).
 */
function FindingsList(props: {
  store: EditorStore;
  findings: readonly ValidationFinding[];
  canEdit: boolean;
}) {
  const { store, findings, canEdit } = props;

  async function onDropWidget(widgetId: string, label: string) {
    const ok = await showConfirm({
      title: `Widget "${label}" löschen?`,
      message:
        'Dieses Widget liegt auf keinem Bild und ist deshalb nirgends zu sehen. '
        + 'Seine Einstellungen und Bindungen gehen dabei verloren. '
        + 'Rückgängig holt es zurück, solange du nicht gespeichert hast.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    store.apply(removeWidget(store.project.peek(), widgetId));
  }

  return (
    <ul class="hv-hmi-editor__findings">
      {findings.map((finding) => {
        const orphanId = canEdit && finding.code === 'widget-unplaced'
          ? finding.widgetId
          : undefined;
        return (
          <li
            key={`${finding.path}:${finding.code}`}
            class={`hv-hmi-editor__finding${
              finding.severity === 'error' ? ' hv-hmi-editor__finding--error' : ''}`}
          >
            <span>{finding.message}</span>
            {orphanId !== undefined && (
              <button
                type="button"
                class="hv-btn"
                onClick={() => onDropWidget(
                  orphanId,
                  findWidget(store.project.peek(), orphanId)?.label ?? orphanId,
                )}
              >
                Widget löschen
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}

// ------------------------------------------------------------- Bilderleiste

function ScreenBar({ store, canEdit }: { store: EditorStore; canEdit: boolean }) {
  const project = store.project.value;
  const activeId = store.activeScreenId.value;

  async function onDelete(screenId: string, label: string) {
    const ok = await showConfirm({
      title: `Bild "${label}" löschen?`,
      message:
        'Widgets, die nur auf diesem Bild liegen, werden mitgelöscht. ' +
        'Widgets, die auch auf einem anderen Bild stehen, bleiben erhalten.',
      confirmLabel: 'Löschen',
      danger: true,
    });
    if (!ok) return;
    store.apply(removeScreen(store.project.peek(), screenId));
  }

  /**
   * Bild duplizieren und gleich hinschalten.
   *
   * Ohne das Hinschalten sieht der Autor nur, dass ein zweiter Reiter
   * entstanden ist, und muss ihn selbst suchen. Die Auswahl wird geleert:
   * sie zeigte auf ein Widget des Originals, und das steht auf dem neuen Bild
   * nicht (es ist dort eine Kopie mit eigener Id).
   */
  function onDuplicate(screenId: string) {
    const result = duplicateScreen(store.project.peek(), screenId);
    store.apply(result.project);
    store.selection.value = [];
    store.activeScreenId.value = result.screenId;
  }

  function onRename(screenId: string, current: string) {
    const next = window.prompt('Wie soll das Bild heißen?', current);
    if (next === null) return;
    const trimmed = next.trim();
    if (trimmed.length === 0 || trimmed === current) return;
    store.apply(renameScreen(store.project.peek(), screenId, trimmed));
  }

  return (
    <div class="hv-hmi-editor__screens" role="tablist" aria-label="Bilder">
      {project.screens.map((screen, index) => (
        <div key={screen.id} class={`hv-hmi-editor__screen${screen.id === activeId ? ' is-active' : ''}`}>
          <button
            type="button"
            role="tab"
            aria-selected={screen.id === activeId}
            onClick={() => { store.activeScreenId.value = screen.id; }}
            onDblClick={() => canEdit && onRename(screen.id, screen.label)}
            title={canEdit ? 'Doppelklick benennt um' : undefined}
          >
            {screen.label}
            <span class="hv-hmi-editor__count">{screen.widgetIds.length}</span>
          </button>
          {canEdit && (
            <span class="hv-hmi-editor__screenTools">
              <button type="button" title="Nach links" disabled={index === 0}
                      onClick={() => store.apply(moveScreen(store.project.peek(), screen.id, index - 1))}>
                ‹
              </button>
              <button type="button" title="Nach rechts" disabled={index === project.screens.length - 1}
                      onClick={() => store.apply(moveScreen(store.project.peek(), screen.id, index + 1))}>
                ›
              </button>
              <button type="button" title="Bild duplizieren"
                      onClick={() => onDuplicate(screen.id)}>
                ⧉
              </button>
              <button type="button" title="Bild löschen"
                      disabled={project.screens.length <= 1}
                      onClick={() => onDelete(screen.id, screen.label)}>
                ✕
              </button>
            </span>
          )}
        </div>
      ))}

      {canEdit && (
        <button
          type="button"
          class="hv-hmi-editor__addScreen"
          onClick={() => {
            const next = addScreen(store.project.peek(), `Bild ${store.project.peek().screens.length + 1}`);
            store.apply(next);
            store.activeScreenId.value = next.screens[next.screens.length - 1].id;
          }}
        >
          + Bild
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------ Helfer

function suggestProjectName(projects: readonly HmiProjectSummary[]): string {
  const base = 'HMI';
  if (!projects.some((p) => p.name === base)) return base;
  for (let i = 2; i < 100; i += 1) {
    const candidate = `${base} ${i}`;
    if (!projects.some((p) => p.name === candidate)) return candidate;
  }
  return `${base} ${Date.now()}`;
}

function describeError(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: string } | null;
    return body?.error ?? err.message;
  }
  return err instanceof Error ? err.message : String(err);
}
